/**
 * 나 탭 맨 아래 계정 (시안 06 M1) + 로그아웃 확인(M2).
 *
 *   라벨 「계정」 → 카드[로그인 방식(로고 24 + 「{방식}로 로그인 중」 / 이메일) + 로그아웃 아이콘]
 *   → 카드 아래 가운데 「회원 탈퇴」(13, 밑줄, 터치 44)
 *
 * - 로그인 방식은 서버(/me/account)가 정합니다: 소셜로 들어왔으면 그 방식(D108), 아니면 이메일(숨은 로그인 · 예전 계정)
 * - 로고는 시작 화면 버튼(components/SocialButtons.jsx)과 같은 그림입니다
 * - M2: role="alertdialog", 열리면 포커스를 [취소]로, 딤 탭 = 취소. 확인 → 세션 지움 → 시작 화면(뒤로 불가)
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LogOut, Mail } from 'lucide-react';
import { Button } from '../../components/Button.jsx';
import { SOCIAL_BUTTONS } from '../../components/SocialButtons.jsx';

const logoOf = (name) => SOCIAL_BUTTONS.find((button) => button.name === name)?.logo ?? null;

const PROVIDERS = {
    email: {
        label: '이메일',
        icon: <Mail size={14} strokeWidth={2} aria-hidden="true" />,
        tone: 'bg-surface-sunken text-text-primary',
    },
    kakao: { label: '카카오', icon: logoOf('kakao'), tone: 'bg-[#FEE500] [&_svg]:size-4' },
    apple: { label: 'Apple', icon: logoOf('apple'), tone: 'border border-hairline bg-white [&_svg]:size-4' },
    google: { label: 'Google', icon: logoOf('google'), tone: 'border border-hairline bg-white [&_svg]:size-4' },
};

export function AccountSection({ account, email, onLogout, onDelete }) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const provider = PROVIDERS[account?.provider] ?? PROVIDERS.email;
    const shownEmail = account?.email ?? email ?? null;
    return (
        <section className="flex flex-col gap-2">
            <h2 className="m-0 text-section text-label-muted">계정</h2>
            <div className="rounded-card border border-hairline bg-surface">
                <div className="flex min-h-16 items-center gap-2.5 px-4 py-2">
                    <span
                        aria-hidden="true"
                        className={`flex size-6 shrink-0 items-center justify-center rounded-md ${provider.tone}`}
                    >
                        {provider.icon}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                        <span className="text-[15px] font-semibold">{provider.label}로 로그인 중</span>
                        {shownEmail ? (
                            <span className="truncate text-[12px] text-text-secondary">{shownEmail}</span>
                        ) : null}
                    </span>
                    <button
                        type="button"
                        aria-label="로그아웃"
                        title="로그아웃"
                        onClick={() => setConfirmOpen(true)}
                        className="flex size-10 shrink-0 items-center justify-center rounded-full text-text-secondary active:bg-surface-sunken"
                    >
                        <LogOut size={19} strokeWidth={1.8} aria-hidden="true" />
                    </button>
                </div>
            </div>
            <button
                type="button"
                onClick={onDelete}
                className="h-11 self-center px-3 text-[13px] text-text-secondary underline underline-offset-[3px]"
            >
                회원 탈퇴
            </button>
            {confirmOpen ? <LogoutDialog onCancel={() => setConfirmOpen(false)} onConfirm={onLogout} /> : null}
        </section>
    );
}

/** M2 로그아웃 확인 */
export function LogoutDialog({ onCancel, onConfirm }) {
    const cancelRef = useRef(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        cancelRef.current?.focus();
        const handle = (event) => {
            if (event.key === 'Escape') {
                onCancel();
            }
        };
        document.addEventListener('keydown', handle);
        return () => document.removeEventListener('keydown', handle);
    }, [onCancel]);

    async function confirm() {
        setBusy(true);
        await onConfirm();
    }

    return createPortal(
        <div className="fixed inset-0 z-50 mx-auto flex max-w-app items-center justify-center px-8">
            <button
                type="button"
                aria-label="취소"
                tabIndex={-1}
                onClick={onCancel}
                className="absolute inset-0 bg-[rgba(38,35,31,0.4)]"
            />
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="logout-title"
                aria-describedby="logout-body"
                className="relative flex w-full flex-col gap-2 rounded-[20px] bg-surface px-5 pt-6 pb-4"
            >
                <span id="logout-title" className="text-[18px] leading-[26px] font-bold">
                    로그아웃할까요?
                </span>
                <span id="logout-body" className="text-[14px] leading-[22px] text-text-secondary">
                    대화와 수첩은 그대로 남아요. 다시 로그인하면 이어서 볼 수 있어요.
                </span>
                <div className="mt-3 flex gap-2">
                    <button
                        ref={cancelRef}
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="h-12 flex-1 rounded-card border border-hairline text-[15px] font-semibold text-text-primary disabled:opacity-40"
                    >
                        취소
                    </button>
                    <Button className="flex-1" busy={busy} onClick={confirm}>
                        로그아웃
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
}
